import '@theme/product-form';
import { StandardEvents } from '@shopify/events';

let instanceNumber = 0;

export class GhostCardPurchase extends HTMLElement {
  connectedCallback() {
    this._listeners?.abort();
    this._listeners = new AbortController();
    this._instance ||= ++instanceNumber;
    this._loading = false;
    this._adding = false;
    const { signal } = this._listeners;


    // Prevent the card's click handler from navigating when using the controls.
    this.addEventListener('mousedown', event => {
      event.stopPropagation();
    }, { signal });
    this.addEventListener('click', event => event.stopPropagation(), { signal });
    this.addEventListener('change', event => {
      if (event.target.matches('select[data-ghost-option]')) {
        event.stopPropagation();
        this.changeOption(event.target);
      }
    }, { signal });
    this.addEventListener('submit', event => {
      if (this._loading || this._adding || this.content?.dataset.canPurchase !== 'true') {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { capture: true, signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, event => {
      if (event.action !== 'add' || !event.promise) return;
      this._adding = true;
      this.setStatus('Adding to cart…');
      this.updateDisabledState();
      event.promise.then(({ detail }) => {
        if (this.isConnected) this.setStatus(detail?.didError ? '' : 'Added to your cart.');
      }).catch(() => {
        if (this.isConnected) {
          this.setStatus('Could not confirm the update. Check your cart before trying again.');
        }
      }).finally(() => {
        this._adding = false;
        if (this.isConnected) this.updateDisabledState();
      });
    }, { signal });

    Promise.all([
      customElements.whenDefined('product-form-component'),
      customElements.whenDefined('add-to-cart-component')
    ]).then(() => {
      if (!this.isConnected || signal.aborted) return;
      this.syncContent();
    });
  }

  disconnectedCallback() {
    this._listeners?.abort();
    this._request?.abort();
    this._generation = (this._generation || 0) + 1;
  }

  get content() {
    return this.querySelector('[data-ghost-purchase-content]');
  }

  setStatus(message) {
    const status = this.querySelector('[data-ghost-status]');
    if (status) status.textContent = message;
  }

  updateDisabledState() {
    const button = this.querySelector('[data-ghost-add]');
    const busy = this._loading || this._adding;
    if (button) button.disabled = busy || this.content?.dataset.canPurchase !== 'true';
    for (const select of this.querySelectorAll('select[data-ghost-option]')) {
      select.disabled = this._adding;
    }
    this.setAttribute('aria-busy', String(busy));
  }

  syncContent() {
    const content = this.content;
    if (!content) return;
    const formComponent = content.querySelector('product-form-component');
    if (formComponent) formComponent.id = 'GhostCardPurchaseForm-' + this._instance;
    this.updateDisabledState();

    // Keep the merchant's price-block typography, alignment and padding.
    const overlay = this.closest('.product-media-overlay');
    const price = content.querySelector('template[data-ghost-price]');
    if (price && overlay) {
      for (const container of overlay.querySelectorAll('product-price [ref="priceContainer"]')) {
        container.replaceChildren(price.content.cloneNode(true));
        container.closest('product-price').dataset.productId = content.dataset.productId;
      }
    }

    // Follow the selected child product when the shopper opens the card.
    const card = this.closest('product-card');
    const destination = new URL(content.dataset.purchaseUrl || content.dataset.productUrl, location.origin);
    if (destination.origin !== location.origin) return;
    if (content.dataset.variantId) destination.searchParams.set('variant', content.dataset.variantId);
    for (const link of card?.querySelectorAll('a[ref="productCardLink"], a[ref="productTitleLink"], a[ref="cardGalleryLink"]') || []) {
      link.href = destination.href;
    }
  }

  buildSelectionURL(changedSelect) {
    const selected = changedSelect.selectedOptions[0];
    // Combined Listings supplies the child URL via product_option_value.product_url.
    const destination = selected?.dataset.productUrl || this.content.dataset.productUrl;
    const url = new URL(destination, location.origin);
    if (url.origin !== location.origin) throw new Error('Unexpected product origin');
    url.searchParams.delete('variant');
    url.searchParams.set('section_id', 'ghost-card-purchase-data');
    const values = [...this.querySelectorAll('[data-ghost-option]')]
      .sort((a, b) => Number(a.dataset.position) - Number(b.dataset.position))
      .map(input => input.value)
      .filter(Boolean);
    url.searchParams.set('option_values', values.join(','));
    return url;
  }

  async changeOption(select) {
    if (this._adding) return;
    this._request?.abort();
    this._request = new AbortController();
    const generation = this._generation = (this._generation || 0) + 1;
    this._loading = true;
    this.setStatus('Updating selection…');
    this.updateDisabledState();
    const position = select.dataset.position;

    try {
      const url = this.buildSelectionURL(select);
      const response = await fetch(url, {
        credentials: 'same-origin',
        signal: this._request.signal,
        headers: { Accept: 'text/html' }
      });
      if (!response.ok) throw new Error('Product request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const next = html.querySelector('[data-ghost-purchase-content]');
      if (!next?.dataset.productId || !next.querySelector('product-form-component')) {
        throw new Error('Purchase section missing');
      }
      if (generation !== this._generation || !this.isConnected) return;
      const restoreFocus = this.contains(document.activeElement);
      this.content.replaceWith(document.importNode(next, true));
      this.syncContent();
      this.setStatus('');
      if (restoreFocus) {
        const control = [...this.querySelectorAll('select[data-ghost-option]')]
          .find(input => input.dataset.position === position);
        control?.focus({ preventScroll: true });
      }
    } catch (error) {
      if (error.name === 'AbortError' || generation !== this._generation) return;
      // The old variant remains valid: restore its option selection as well.
      for (const control of this.querySelectorAll('select[data-ghost-option]')) {
        for (const option of control.options) option.selected = option.defaultSelected;
      }
      this.setStatus('Could not load that option. Your previous selection has been restored. Please try again.');
    } finally {
      if (generation === this._generation && this.isConnected) {
        this._loading = false;
        this.updateDisabledState();
      }
    }
  }
}

if (!customElements.get('ghost-card-purchase')) {
  customElements.define('ghost-card-purchase', GhostCardPurchase);
}
